import { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, CheckIcon, ArrowLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import NotebookCard from '../shared/notebook-card';
import axios from 'axios';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { useRef } from 'react';

import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import MyNotebooksLoader from '../../components/loaders/MyNotebookLoader';

const getSubjects = async (packId) => {
  try {
    const response = await axios.get(`/api/packs/${packId}/subjects`);

    // Validate response data
    const responseData = response.data.data || response.data;
    if (!Array.isArray(responseData)) {
      throw new Error('Invalid subjects response format');
    }

    const normalizedSubjects = responseData.map((subject) => ({
      id: subject._id || subject.id,
      _id: subject._id || subject.id,
      name: subject.title || subject.name || subject.shortName || 'Unknown Subject',
      title: subject.title || subject.name || subject.shortName || 'Unknown Subject',
      color: subject.color || '#4F46E5',
      icon: subject.icon || '/subject-icon.svg',
      shortName: subject.shortName,
      gradient: subject.gradient,
      gradientDark: subject.gradientDark,
    }));

    return normalizedSubjects;
  } catch (error) {
    throw error;
  }
};

const getChapterWiseNotebooks = async (packId, subjectId) => {
  try {
    const response = await axios.get(`/api/v2/notebooks/${packId}/${subjectId}/chapter-wise`);

    // Handle the actual API response structure
    const responseData = response.data;

    // Check if the response has the expected structure
    if (!responseData || !responseData.data || !responseData.data.chapterNotebooks) {
      return [];
    }

    const chapterNotebooks = responseData.data.chapterNotebooks;
    const units = responseData.data.units || [];

    // Transform the chapter notebooks to match your component's expected format
    const normalizedNotebooks = chapterNotebooks.map((notebook, index) => ({
      id: notebook.chapterId || `chapter-${index}`,
      _id: notebook.chapterId || `chapter-${index}`,
      title: notebook.title || 'Untitled Chapter',
      name: notebook.title || 'Untitled Chapter',
      questionCount: notebook.questions ? notebook.questions.length : 0,
      unit: notebook.unitId ? `Unit ${units.indexOf(notebook.unitId) + 1}` : 'Unit 1',
      lastUsed: new Date().toISOString(), // API doesn't provide this, so use current time
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      type: 'chapter',
      subjectId: subjectId,
      packId: packId,
      chapterId: notebook.chapterId,
      unitId: notebook.unitId,
      questions: notebook.questions || [],
      isArchived: false,
      isPinned: false,
    }));

    return normalizedNotebooks;
  } catch (error) {
    // If it's a 404 or similar, returning empty array instead of throwing
    if (error.response?.status === 404) {
      return [];
    }

    throw error;
  }
};

export const ChapterWise = ({ onNotebookClick, onScreenChange, onBack, selectedPack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubject, setActiveSubject] = useState(null);
  const [sortBy, setSortBy] = useState('Default');
  const [selectedUnit, setSelectedUnit] = useState('Unit');
  const [subjects, setSubjects] = useState([]);
  const [chapterNotebooks, setChapterNotebooks] = useState({});
  const [loading, setLoading] = useState(false);
  const [unitOptions, setUnitOptions] = useState(['Unit']);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const sortOptions = ['Default', 'Last Used', 'Qs (High to Low)', 'Qs (Low to High)', 'A-Z', 'Z-A'];

  // Get pack ID from props or localStorage
  const packId = selectedPack?.id || localStorage.getItem('selectedPackId');

  useEffect(() => {
    if (!packId) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const subjectsData = await getSubjects(packId);

        setSubjects(subjectsData);

        if (subjectsData.length > 0) {
          setActiveSubject(subjectsData[0].id);
          await fetchChapterNotebooks(subjectsData[0].id);
        }
      } catch (err) {
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedPack, packId]);

  const fetchChapterNotebooks = async (subjectId) => {
    try {
      setLoading(true);
      setError(null);

      const data = await getChapterWiseNotebooks(packId, subjectId);

      setChapterNotebooks((prev) => {
        const newState = {
          ...prev,
          [subjectId]: data,
        };

        return newState;
      });

      // Extract unique units from the notebooks
      const units = [...new Set(data.map((n) => n.unit))].filter(Boolean);

      setUnitOptions(['Unit', ...units]);
    } catch (err) {
      setError('Failed to load notebooks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = async (subjectId) => {
    setActiveSubject(subjectId);
    setSelectedUnit('Unit'); // Reset unit filter when changing subject

    if (!chapterNotebooks[subjectId]) {
      await fetchChapterNotebooks(subjectId);
    } else {
      // Update unit options for cached data
      const data = chapterNotebooks[subjectId];
      const units = [...new Set(data.map((n) => n.unit))].filter(Boolean);
      setUnitOptions(['Unit', ...units]);
    }
  };

  const currentNotebooks = activeSubject ? chapterNotebooks[activeSubject] || [] : [];
  const filteredNotebooks = currentNotebooks.filter((notebook) =>
    notebook.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedNotebooks = [...filteredNotebooks].sort((a, b) => {
    switch (sortBy) {
      case 'Qs (High to Low)':
        return (b.questionCount || 0) - (a.questionCount || 0);
      case 'Qs (Low to High)':
        return (a.questionCount || 0) - (b.questionCount || 0);
      case 'A-Z':
        return a.title.localeCompare(b.title);
      case 'Z-A':
        return b.title.localeCompare(a.title);
      case 'Last Used':
        return new Date(b.lastUsed || b.updatedAt || 0) - new Date(a.lastUsed || a.updatedAt || 0);
      default:
        return 0;
    }
  });

  const scrollRight = () => {
    if (scrollRef.current) {
      // Check if we can scroll more
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      if (scrollLeft + clientWidth < scrollWidth) {
        const scrollAmount = window.innerWidth < 768 ? 100 : 150;
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  // Add this function to check if scroll arrow should be visible
  const shouldShowScrollArrow = () => {
    if (!scrollRef.current) return false;
    const { scrollWidth, clientWidth } = scrollRef.current;
    return scrollWidth > clientWidth;
  };

  const displayedNotebooks =
    selectedUnit !== 'Unit' ? sortedNotebooks.filter((notebook) => notebook.unit === selectedUnit) : sortedNotebooks;

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">Error Loading Data</h3>
          <p className="text-[var(--q3-neutral-light)] mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-white px-6 py-2 transition-colors duration-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Get the active subject data for the tab color
  const activeSubjectData = subjects.find((s) => s.id === activeSubject);
  const tabColor = activeSubjectData?.color || '#4F46E5';

  return (
    <div className="h-screen md:h-auto flex flex-col">
      {/* Top Section (scroll-locked on mobile) */}
      <div className="flex-1 p-4 md:p-6 md:overflow-visible overflow-hidden flex flex-col">
        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-30 bg-[var(--q3-surface-default)] pb-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            {onBack && (
              <button onClick={onBack} className="p-2">
                <ArrowLeftIcon className="w-5 h-5 text-[var(--q3-neutral-default)]" />
              </button>
            )}

            <div className="flex-1 text-center">
              <h1 className="text-xl font-bold text-[var(--q3-neutral-default)]">Chapter-wise Notebooks</h1>
              <p className="text-[10px] text-[var(--q3-neutral-light)]">
                All your bookmarked Qs categorized chapter wise
              </p>
            </div>

            <div className="p-2 opacity-0">
              <MagnifyingGlassIcon className="w-5 h-5" />
            </div>
          </div>

          {/* Mobile Search Box */}
          <div className="relative w-full px-2 mb-4">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[var(--q3-neutral-light)] w-5 h-5" />
            <input
              type="text"
              placeholder="Search Notebook"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] placeholder-[var(--q3-neutral-light)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)] focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex flex-col md:flex-row md:items-start justify-between gap-2 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)] mb-1">Chapter-wise Notebooks</h1>
            <p className="text-[var(--q3-neutral-light)]">All your bookmarked Qs categorized Chapter-wise</p>
          </div>

          {/* Desktop Search Bar */}
          <div className="ml-auto md:mt-2">
            <div className="relative w-52 md:w-60">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--q3-neutral-light)] w-5 h-5" />
              <input
                type="text"
                placeholder="Search Notebook"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] placeholder-[var(--q3-neutral-light)] focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)] focus:border-transparent text-sm mr-4"
              />
            </div>
          </div>
        </div>

        {/* Filters Section (Sticky in mobile) */}
        <div className="z-20 bg-[var(--q3-surface-default)] pb-4 md:pb-0 mb-4 md:mb-6">
          {/* Scrollable Tabs */}
          <div className="overflow-x-auto pr-8">
            <TabsContainer color={tabColor} className="flex gap-4">
              {subjects.map((subject) => (
                <Tab
                  key={subject.id}
                  isActive={activeSubject === subject.id}
                  color={activeSubject === subject.id ? subject.color : 'var(--q3-neutral-light)'}
                  tabColor={subject.color}
                  onClick={() => handleSubjectChange(subject.id)}
                >
                  {subject.icon && <img src={subject.icon} alt={subject.title} className="h-8 w-8" />}
                  <p className="mb-0">{subject.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>

          {/* Sort and Filter - Updated to have single scrollable container */}
          <div className="relative mt-4">
            <div
              ref={scrollRef}
              className="flex items-center gap-4 flex-nowrap overflow-x-auto pr-12"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitScrollbar: { display: 'none' },
              }}
            >
              {/* Unit Dropdown */}
              <div className="flex items-center flex-shrink-0">
                <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                  <SelectTrigger className={`text-sm rounded-full px-4 py-2 pr-4 text-[var(--q3-neutral-default)] `}>
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>

                  <SelectContent>
                    {unitOptions.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="h-6 border-l border-[var(--q3-stroke-normal)] flex-shrink-0"></div>

              <span className="text-sm text-[var(--q3-neutral-default)] flex-shrink-0">Sort by</span>

              {/* Sort Options */}
              {sortOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => setSortBy(option)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm whitespace-nowrap flex-shrink-0 transition-colors cursor-pointer ${
                    sortBy === option
                      ? 'bg-[var(--q3-accent-normal)] text-white'
                      : 'bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dimmest)] border border-[var(--q3-stroke-normal)]'
                  }`}
                >
                  <span>{option}</span>
                  {sortBy === option && <CheckIcon size={12} weight="bold" />}
                </button>
              ))}
            </div>

            {/* Fixed Scroll Arrow for entire filter line */}
            <div className="absolute right-0 top-0 h-full bg-gradient-to-l from-[var(--q3-surface-default)] via-[var(--q3-surface-default)] to-transparent w-12 flex items-center justify-end pointer-events-none">
              <button
                onClick={scrollRight}
                className="pointer-events-auto bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center z-20"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <CaretRightIcon size={14} className="text-[var(--q3-neutral-default)]" weight="bold" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pb-6 md:overflow-visible">
          {loading ? (
            <MyNotebooksLoader />
          ) : displayedNotebooks.length > 0 ? (
            <div className="px-2 sm:px-4 md:px-6 lg:px-8 pb-6">
              <div className="flex justify-center sm:justify-start">
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-6">
                  {displayedNotebooks.map((notebook) => (
                    <NotebookCard
                      key={notebook.id}
                      title={notebook.title}
                      count={notebook.questionCount}
                      notebook={notebook}
                      onClick={() =>
                        onNotebookClick({
                          ...notebook,
                          type: 'chapter',
                          subject: subjects.find((s) => s.id === activeSubject)?.name,
                        })
                      }
                      className="hover:scale-105 transition-transform duration-200"
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-2">😕 No notebooks found</h3>
              <p className="text-[var(--q3-neutral-light)] mb-6">
                {searchTerm || selectedUnit !== 'Unit'
                  ? 'Try adjusting your search or filter criteria'
                  : 'No chapter-wise notebooks available for this subject'}
              </p>
              {(searchTerm || selectedUnit !== 'Unit' || sortBy !== 'Default') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedUnit('Unit');
                    setSortBy('Default');
                  }}
                  className="rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-white px-6 py-2 transition-colors duration-300"
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
